import { assign, setup } from 'xstate';
import type { SchedulesEvent } from './events';

export type RouteId = 'E' | '44';

export type ScheduleStop = {
  id: string;
  name: string;
  times: string[];
};

export type ScheduleDirection = {
  id: string;
  label: string;
  stops: ScheduleStop[];
};

export type ScheduleRoute = {
  id: RouteId;
  name: string;
  badge: string;
  directions: ScheduleDirection[];
};

export type Arrival = {
  scheduledTime: string;
  label: string;
  serviceDay: string;
  minutesUntil: number;
};

export type SchedulesContext = {
  routes: ScheduleRoute[];
  selectedRouteId: RouteId | null;
  selectedDirectionId: string | null;
  selectedStopId: string | null;
  arrivals: Arrival[];
  arrivalStatus: 'idle' | 'ready' | 'missing';
  rememberedSelection: {
    routeId: RouteId;
    directionId: string;
    stopId: string;
  } | null;
  lastRefreshedAt: string | null;
};

// The compact local timetable is intentionally data-shaped: it is the source for this
// browser-only instrument, rather than placeholder rows standing in for a server read.
export const SEATTLE_SCHEDULES: ScheduleRoute[] = [
  {
    id: 'E',
    name: 'RapidRide E Line',
    badge: 'E',
    directions: [
      {
        id: 'e-aurora-downtown',
        label: 'Aurora Village → Downtown Seattle',
        stops: [
          {
            id: 'e-aurora-village',
            name: 'Aurora Village Transit Center',
            times: [
              '05:00',
              '05:25',
              '05:50',
              '06:15',
              '06:40',
              '07:05',
              '08:00',
              '09:00',
              '10:00',
              '11:00',
              '12:00',
              '13:00',
              '14:00',
              '15:00',
              '16:00',
              '17:00',
              '18:00',
              '19:00',
              '20:00',
              '21:00',
              '22:00',
              '23:00',
              '23:40',
              '00:20',
              '01:10',
              '02:10',
            ],
          },
          {
            id: 'e-aurora-130th',
            name: 'Aurora Ave N & N 130th St',
            times: [
              '05:08',
              '05:33',
              '05:58',
              '06:23',
              '06:48',
              '07:13',
              '08:08',
              '09:08',
              '10:08',
              '11:08',
              '12:08',
              '13:08',
              '14:08',
              '15:08',
              '16:08',
              '17:08',
              '18:08',
              '19:08',
              '20:08',
              '21:08',
              '22:08',
              '23:08',
              '23:48',
              '00:28',
              '01:18',
              '02:18',
            ],
          },
          {
            id: 'e-3rd-pike',
            name: '3rd Ave & Pike St',
            times: [
              '05:38',
              '06:03',
              '06:28',
              '06:53',
              '07:18',
              '07:43',
              '08:38',
              '09:38',
              '10:38',
              '11:38',
              '12:38',
              '13:38',
              '14:38',
              '15:38',
              '16:38',
              '17:38',
              '18:38',
              '19:38',
              '20:38',
              '21:38',
              '22:38',
              '23:38',
              '00:18',
              '01:08',
              '01:58',
              '02:48',
            ],
          },
        ],
      },
      {
        id: 'e-downtown-aurora',
        label: 'Downtown Seattle → Aurora Village',
        stops: [
          {
            id: 'e-3rd-pine',
            name: '3rd Ave & Pine St',
            times: [
              '05:10',
              '05:35',
              '06:00',
              '06:25',
              '06:50',
              '07:15',
              '08:10',
              '09:10',
              '10:10',
              '11:10',
              '12:10',
              '13:10',
              '14:10',
              '15:10',
              '16:10',
              '17:10',
              '18:10',
              '19:10',
              '20:10',
              '21:10',
              '22:10',
              '23:10',
              '23:50',
              '00:30',
              '01:20',
              '02:20',
            ],
          },
          {
            id: 'e-aurora-105',
            name: 'Aurora Ave N & N 105th St',
            times: [
              '05:42',
              '06:07',
              '06:32',
              '06:57',
              '07:22',
              '07:47',
              '08:42',
              '09:42',
              '10:42',
              '11:42',
              '12:42',
              '13:42',
              '14:42',
              '15:42',
              '16:42',
              '17:42',
              '18:42',
              '19:42',
              '20:42',
              '21:42',
              '22:42',
              '23:42',
              '00:22',
              '01:12',
              '02:02',
              '02:52',
            ],
          },
          {
            id: 'e-aurora-village-return',
            name: 'Aurora Village Transit Center',
            times: [
              '06:12',
              '06:37',
              '07:02',
              '07:27',
              '07:52',
              '08:17',
              '09:12',
              '10:12',
              '11:12',
              '12:12',
              '13:12',
              '14:12',
              '15:12',
              '16:12',
              '17:12',
              '18:12',
              '19:12',
              '20:12',
              '21:12',
              '22:12',
              '23:12',
              '23:52',
              '00:32',
              '01:22',
              '02:12',
              '02:58',
            ],
          },
        ],
      },
    ],
  },
  {
    id: '44',
    name: 'Route 44',
    badge: '44',
    directions: [
      {
        id: '44-ballard-udistrict',
        label: 'Ballard → University District',
        stops: [
          {
            id: '44-market-24th',
            name: 'NW Market St & 24th Ave NW',
            times: [
              '05:15',
              '05:45',
              '06:15',
              '06:45',
              '07:15',
              '07:45',
              '08:15',
              '09:00',
              '10:00',
              '11:00',
              '12:00',
              '13:00',
              '14:00',
              '15:00',
              '16:00',
              '17:00',
              '18:00',
              '19:00',
              '20:00',
              '21:00',
              '22:00',
              '23:00',
              '23:45',
              '00:30',
              '01:20',
              '02:10',
            ],
          },
          {
            id: '44-45th-wallingford',
            name: 'N 45th St & Wallingford Ave N',
            times: [
              '05:32',
              '06:02',
              '06:32',
              '07:02',
              '07:32',
              '08:02',
              '08:32',
              '09:17',
              '10:17',
              '11:17',
              '12:17',
              '13:17',
              '14:17',
              '15:17',
              '16:17',
              '17:17',
              '18:17',
              '19:17',
              '20:17',
              '21:17',
              '22:17',
              '23:17',
              '00:02',
              '00:47',
              '01:37',
              '02:27',
            ],
          },
          {
            id: '44-university-45th',
            name: 'University Way NE & NE 45th St',
            times: [
              '05:48',
              '06:18',
              '06:48',
              '07:18',
              '07:48',
              '08:18',
              '08:48',
              '09:33',
              '10:33',
              '11:33',
              '12:33',
              '13:33',
              '14:33',
              '15:33',
              '16:33',
              '17:33',
              '18:33',
              '19:33',
              '20:33',
              '21:33',
              '22:33',
              '23:33',
              '00:18',
              '01:03',
              '01:53',
              '02:43',
            ],
          },
        ],
      },
      {
        id: '44-udistrict-ballard',
        label: 'University District → Ballard',
        stops: [
          {
            id: '44-university-43rd',
            name: 'University Way NE & NE 43rd St',
            times: [
              '05:20',
              '05:50',
              '06:20',
              '06:50',
              '07:20',
              '07:50',
              '08:20',
              '09:05',
              '10:05',
              '11:05',
              '12:05',
              '13:05',
              '14:05',
              '15:05',
              '16:05',
              '17:05',
              '18:05',
              '19:05',
              '20:05',
              '21:05',
              '22:05',
              '23:05',
              '23:50',
              '00:35',
              '01:25',
              '02:15',
            ],
          },
          {
            id: '44-45th-wall-return',
            name: 'N 45th St & Wallingford Ave N',
            times: [
              '05:37',
              '06:07',
              '06:37',
              '07:07',
              '07:37',
              '08:07',
              '08:37',
              '09:22',
              '10:22',
              '11:22',
              '12:22',
              '13:22',
              '14:22',
              '15:22',
              '16:22',
              '17:22',
              '18:22',
              '19:22',
              '20:22',
              '21:22',
              '22:22',
              '23:22',
              '00:07',
              '00:52',
              '01:42',
              '02:32',
            ],
          },
          {
            id: '44-ballard-24th-return',
            name: 'NW Market St & 24th Ave NW',
            times: [
              '05:54',
              '06:24',
              '06:54',
              '07:24',
              '07:54',
              '08:24',
              '08:54',
              '09:39',
              '10:39',
              '11:39',
              '12:39',
              '13:39',
              '14:39',
              '15:39',
              '16:39',
              '17:39',
              '18:39',
              '19:39',
              '20:39',
              '21:39',
              '22:39',
              '23:39',
              '00:24',
              '01:09',
              '01:59',
              '02:49',
            ],
          },
        ],
      },
    ],
  },
];

const seattleClock = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '00';
  const hour = Number(value('hour')) % 24;
  return { date: `${value('year')}-${value('month')}-${value('day')}`, minute: hour * 60 + Number(value('minute')) };
};

const previousDate = (date: string) => {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() - 1);
  return value.toISOString().slice(0, 10);
};

const formatTime = (time: string) => {
  const [hourText, minute] = time.split(':');
  const hour = Number(hourText);
  return `${hour % 12 || 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const findSelection = (context: SchedulesContext) => {
  const route = context.routes.find((item) => item.id === context.selectedRouteId);
  const direction = route?.directions.find((item) => item.id === context.selectedDirectionId);
  const stop = direction?.stops.find((item) => item.id === context.selectedStopId);
  return { route, direction, stop };
};

const calculateArrivals = (
  context: SchedulesContext,
): Pick<SchedulesContext, 'arrivals' | 'arrivalStatus' | 'lastRefreshedAt'> => {
  const { stop } = findSelection(context);
  const clock = seattleClock();
  if (!stop) return { arrivals: [], arrivalStatus: 'idle', lastRefreshedAt: new Date().toISOString() };
  const early = clock.minute < 180;
  const currentComparable = clock.minute + (early ? 1440 : 0);
  const serviceDay = early ? previousDate(clock.date) : clock.date;
  const arrivals = stop.times
    .map((scheduledTime) => {
      const raw = Number(scheduledTime.slice(0, 2)) * 60 + Number(scheduledTime.slice(3));
      const comparable = raw < 180 ? raw + 1440 : raw + (early ? 2880 : 0);
      const dayOffset = raw < 180 ? 0 : early ? 1 : 0;
      const date = new Date(`${serviceDay}T12:00:00Z`);
      date.setUTCDate(date.getUTCDate() + dayOffset);
      return {
        scheduledTime,
        label: formatTime(scheduledTime),
        serviceDay: date.toISOString().slice(0, 10),
        minutesUntil: comparable - currentComparable,
      };
    })
    .filter((arrival) => arrival.minutesUntil >= 0)
    .slice(0, 3);
  return {
    arrivals,
    arrivalStatus: arrivals.length > 0 ? 'ready' : 'missing',
    lastRefreshedAt: new Date().toISOString(),
  };
};

const initialContext: SchedulesContext = {
  routes: SEATTLE_SCHEDULES,
  selectedRouteId: null,
  selectedDirectionId: null,
  selectedStopId: null,
  arrivals: [],
  arrivalStatus: 'idle',
  rememberedSelection: null,
  lastRefreshedAt: null,
};

const schedulesSetup = setup({
  types: {
    context: {} as SchedulesContext,
    events: {} as SchedulesEvent,
  },
  guards: {
    hasSelection: ({ context }) => findSelection(context).stop !== undefined,
    validDirection: ({ context, event }) =>
      event.type !== 'schedule.choose-direction' ||
      context.routes.some(
        (route) =>
          route.id === context.selectedRouteId &&
          route.directions.some((direction) => direction.id === event.directionId),
      ),
    validStop: ({ context, event }) =>
      event.type !== 'schedule.choose-stop' ||
      context.routes.some(
        (route) =>
          route.id === context.selectedRouteId &&
          route.directions.some(
            (direction) =>
              direction.id === context.selectedDirectionId && direction.stops.some((stop) => stop.id === event.stopId),
          ),
      ),
  },
  actions: {
    chooseRoute: assign(({ context, event }) =>
      event.type === 'schedule.choose-route'
        ? {
            ...context,
            selectedRouteId: event.routeId,
            selectedDirectionId: null,
            selectedStopId: null,
            arrivals: [],
            arrivalStatus: 'idle',
          }
        : context,
    ),
    chooseDirection: assign(({ context, event }) =>
      event.type === 'schedule.choose-direction'
        ? {
            ...context,
            selectedDirectionId: event.directionId,
            selectedStopId: null,
            arrivals: [],
            arrivalStatus: 'idle',
          }
        : context,
    ),
    chooseStop: assign(({ context, event }) =>
      event.type === 'schedule.choose-stop'
        ? {
            ...context,
            selectedStopId: event.stopId,
            ...calculateArrivals({ ...context, selectedStopId: event.stopId }),
          }
        : context,
    ),
    rememberChoice: assign(({ context }) => {
      const selection = findSelection(context);
      return selection.route && selection.direction && selection.stop
        ? {
            ...context,
            rememberedSelection: {
              routeId: selection.route.id,
              directionId: selection.direction.id,
              stopId: selection.stop.id,
            },
            ...calculateArrivals(context),
          }
        : context;
    }),
    refreshArrivals: assign(({ context }) => ({ ...context, ...calculateArrivals(context) })),
  },
});

export const schedulesMachine = schedulesSetup.createMachine({
  id: 'schedules',
  initial: 'choosing',
  context: initialContext,
  states: {
    choosing: {
      on: {
        'schedule.choose-route': { actions: 'chooseRoute' },
        'schedule.choose-direction': { guard: 'validDirection', actions: 'chooseDirection' },
        'schedule.choose-stop': { target: 'ready', guard: 'validStop', actions: 'chooseStop' },
        'schedule.remember-choice': { target: 'ready', guard: 'hasSelection', actions: 'rememberChoice' },
        'schedule.refresh-arrivals': { target: 'refreshing', guard: 'hasSelection' },
      },
    },
    ready: {
      on: {
        'schedule.choose-route': { target: 'choosing', actions: 'chooseRoute' },
        'schedule.choose-direction': { target: 'choosing', guard: 'validDirection', actions: 'chooseDirection' },
        'schedule.choose-stop': { guard: 'validStop', actions: 'chooseStop' },
        'schedule.remember-choice': { guard: 'hasSelection', actions: 'rememberChoice' },
        'schedule.refresh-arrivals': { target: 'refreshing', guard: 'hasSelection' },
      },
    },
    refreshing: {
      entry: 'refreshArrivals',
      after: { 80: 'ready' },
      on: {
        'schedule.choose-route': { target: 'choosing', actions: 'chooseRoute' },
      },
    },
  },
});
